import { useState } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  Box,
  Text,
  TextField,
  Button,
} from "@medentic/polaris-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useGetSingleCompanyDetails } from "@shared/hooks/common/useGetSingleCompanyDetails";
import { ProcessLoading } from "@shared/ui";
import { ProcessStartResponse } from "@shared/types/processTypes";
import { showToast } from "@shared/lib";

interface TestDataCardProps {
  hcc: string;
  defaultCount?: number;
}

export default function TestDataCard({
  hcc,
  defaultCount = 10,
}: TestDataCardProps) {
  const { t } = useTranslation();
  const { POST, DELETE } = useApiRequest();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [count, setCount] = useState(String(defaultCount));
  const [processId, setProcessId] = useState<string | null>(null);
  const [estimatedTime, setEstimatedTime] = useState<number | undefined>(
    undefined
  );

  const { company, isLoading } = useGetSingleCompanyDetails(hcc);

  // Generate events mutation
  const generateMutation = useMutation({
    mutationFn: () =>
      POST<ProcessStartResponse>(
        `/api/healthcare-companies/${hcc}/e/test-data/generate`,
        {
          count: parseInt(count, 10) || defaultCount,
        }
      ),
    onSuccess: (data) => {
      // Set processId and estimated time to start tracking
      setProcessId(data.processId);
      setEstimatedTime(data.estimatedTimeSeconds);
    },
  });

  const handleProcessComplete = (output?: Record<string, unknown>) => {
    setProcessId(null);
    setEstimatedTime(undefined);
    queryClient.invalidateQueries({ queryKey: ["events", hcc] });
    queryClient.invalidateQueries({ queryKey: ["attendees", hcc] });

    // Show success toast with summary
    if (output) {
      const eventsCreated = output.eventsCreated ?? 0;
      const attendeesCreated = output.attendeesCreated ?? 0;
      dispatch(
        showToast({
          message: t("eventDashboard.testData.successMessage", {
            events: eventsCreated,
            attendees: attendeesCreated,
          }),
          type: "success",
        })
      );
    }
  };

  const handleProcessError = () => {
    // Don't clear processId - let the user see error and retry button
  };

  const handleRetry = () => {
    setProcessId(null);
    setEstimatedTime(undefined);
    // Trigger a new generation
    generateMutation.mutate();
  };

  // Clean test data mutation
  const cleanMutation = useMutation({
    mutationFn: () =>
      DELETE(`/api/healthcare-companies/${hcc}/e/test-data/clean`),
    onSuccess: () => {
      // Reload page to reset everything
      window.location.reload();
    },
  });

  // Don't show card during loading or if not a test company
  if (isLoading || !company?.isTest) {
    return null;
  }

  const isLocked = Boolean(processId) || generateMutation.isPending || cleanMutation.isPending;

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h3">
          {t("eventDashboard.testData.title")}
        </Text>

        {/* Show ProcessLoading when generating */}
        {processId ? (
          <Box background="bg-surface-secondary" padding="400" borderRadius="200">
            <ProcessLoading
              processId={processId}
              estimatedTimeSeconds={estimatedTime}
              refreshTime={2000}
              onComplete={handleProcessComplete}
              onError={handleProcessError}
              onRetry={handleRetry}
            />
          </Box>
        ) : (
          <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
            {/* Generate Section */}
            <Box background="bg-surface-secondary" padding="400" borderRadius="200">
              <BlockStack gap="300">
                <Text variant="bodyMd" fontWeight="semibold" as="p">
                  {t("eventDashboard.testData.generateTitle")}
                </Text>
                <Text variant="bodySm" tone="subdued" as="p">
                  {t("eventDashboard.testData.generateDescription")}
                </Text>
                <InlineStack gap="200" blockAlign="end">
                  <div style={{ width: "80px" }}>
                    <TextField
                      label={t("eventDashboard.testData.countLabel")}
                      type="number"
                      value={count}
                      onChange={setCount}
                      autoComplete="off"
                      min={1}
                      max={50}
                      disabled={isLocked}
                    />
                  </div>
                  <Button
                    onClick={() => generateMutation.mutate()}
                    loading={generateMutation.isPending}
                    disabled={isLocked}
                  >
                    {t("eventDashboard.testData.generate")}
                  </Button>
                </InlineStack>
              </BlockStack>
            </Box>

            {/* Clean Section */}
            <Box background="bg-surface-secondary" padding="400" borderRadius="200">
              <BlockStack gap="300">
                <Text variant="bodyMd" fontWeight="semibold" as="p">
                  {t("eventDashboard.testData.cleanTitle")}
                </Text>
                <Text variant="bodySm" tone="subdued" as="p">
                  {t("eventDashboard.testData.cleanDescription")}
                </Text>
                <Button
                  onClick={() => cleanMutation.mutate()}
                  loading={cleanMutation.isPending}
                  tone="critical"
                  disabled={isLocked}
                >
                  {t("eventDashboard.testData.clean")}
                </Button>
              </BlockStack>
            </Box>
          </InlineGrid>
        )}
      </BlockStack>
    </Card>
  );
}

